import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plane, Calendar, Users, Mail, Phone, CreditCard, ArrowLeft, AlertCircle, AlertTriangle, Luggage, Clock, MapPin, ChevronDown, ChevronUp, Wallet, Building2, Check, X } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import FlightDetailsCard from '../components/FlightDetailsCard';
import FareRulesPanel from '../components/FareRulesPanel';
import BookingPageHeader from '../components/BookingPageHeader';
import { useAgentAuth } from '../context/AgentAuthContext';
import { useAuth } from '../context/AuthContext';
import MobileOTPLogin from '../components/MobileOTPLogin';
import FareRepriceModal from '../components/FareRepriceModal';
import DuplicateBookingPanel from '../components/DuplicateBookingPanel';
import SessionExpiryChip from '../components/SessionExpiryChip';
import ReviewMessagesBanner from '../components/ReviewMessagesBanner';
import useSessionExpiry from '../hooks/useSessionExpiry';
import { getApiBookingRouteId, getUserFacingBookingId } from '../utils/userFacingBookingId';
import { deriveDisplayFare, derivePaxCountsFromPassengers } from '../utils/deriveDisplayFare';
import { deriveCommissionFare, resolveCommissionPreview } from '../utils/deriveCommissionFare';
import FareCommissionRows from '../components/FareCommissionRows';
import { resolveAcceptedNewTotal } from '../utils/fareAlertAcceptance';
import { tripjackSessionBookingId } from '../utils/tripjackSessionBookingId';
import { bookingSessionHeaders } from '../utils/bookingSessionCorrelation';
import { openRazorpayCheckout } from '../utils/razorpayCheckout';
import { enrichOfflineFlight } from '../utils/airportMapper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const looksLikeTripjackPriceId = (v) =>
  typeof v === 'string' && v.includes('~') && v.length > 30;

/** Detect Tripjack from explicit source/supplier or characteristic priceIds (root + flightData + RT legs). */
function isTripjackBookingPayload(bookingData) {
  if (!bookingData) return false;
  const s = String(
    bookingData?.flightData?.source || bookingData?.flightData?.supplier || '',
  ).toLowerCase();
  if (s.includes('tripjack')) return true;
  const ids = [
    bookingData?.priceId,
    bookingData?.flightData?.priceId,
    bookingData?.onwardPriceId,
    bookingData?.returnPriceId,
    ...(Array.isArray(bookingData?.priceIds) ? bookingData.priceIds : []),
  ];
  return ids.some(looksLikeTripjackPriceId);
}

/**
 * Sanitizes raw error text coming from the booking-create response so we
 * never leak internal/system errors (BSON, stack traces, mongo internals)
 * into the user-facing inline banner. Returns:
 *   {
 *     userMessage: string,   // text safe to show
 *     isInternal: boolean,   // true when the error looks like a server
 *                            // bug rather than a user-correctable issue.
 *                            // When true, hide the "Edit Passenger
 *                            // Details" CTA — fixing the form won't help.
 *   }
 */
function sanitizeBookingError(raw) {
  const s = String(raw || '').trim();
  if (!s) {
    return {
      userMessage: 'Something went wrong while creating your booking. Please try again, or contact support if the issue persists.',
      isInternal: true,
    };
  }
  const internalSignals = [
    'documents must have only string keys',
    'invaliddocument',
    'bson',
    'pymongo',
    'motor.',
    'traceback',
    'nonetype',
    'attributeerror',
    'keyerror',
    'typeerror',
    'valueerror',
    "object has no attribute",
    'internal server error',
    'ssl:',
    'http 500',
  ];
  const lower = s.toLowerCase();
  const isInternal = internalSignals.some((sig) => lower.includes(sig));
  if (isInternal) {
    return {
      userMessage: "We hit an unexpected issue while creating your booking. Your payment hasn't been deducted. Please retry in a moment, or contact support and quote the time you saw this error.",
      isInternal: true,
    };
  }
  return { userMessage: s, isInternal: false };
}

const ReviewBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { agent, isAuthenticated: isAgentAuthenticated, getPaymentEligibility } = useAgentAuth();
  const { isAuthenticated: isB2CAuthenticated } = useAuth();

  // B2C OTP gate — when a guest clicks "Proceed to Payment" we hold the
  // checkout open until they complete OTP login. After login we auto-resume
  // the payment so they don't have to click again.
  const [otpLoginOpen, setOtpLoginOpen] = useState(false);
  const [resumePaymentAfterLogin, setResumePaymentAfterLogin] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [baggageProtection, setBaggageProtection] = useState(false);
  const [baggageProtectionPrice, setBaggageProtectionPrice] = useState(0);
  const [addonsExpanded, setAddonsExpanded] = useState(false);
  
  // Agent payment mode states
  const [paymentEligibility, setPaymentEligibility] = useState(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('GATEWAY'); // Default to gateway

  const [fareRepriceOpen, setFareRepriceOpen] = useState(false);
  const [fareRepriceDetail, setFareRepriceDetail] = useState(null);
  const [fareRepriceAttempt, setFareRepriceAttempt] = useState(0);
  /** U1 (Audit follow-up) — Hard latch when supplier exhausts reprice attempts.
   *  Disables all "Pay / Continue / Hold" CTAs so the user can't re-trigger
   *  the dead-end flow with a stale priceId. Cleared only by re-search. */
  const [repriceExhausted, setRepriceExhausted] = useState(false);
  /** U2 — `session_expires_at` driven hard-block at supplier TTL (Audit §6.6). */
  const [sessionExpired, setSessionExpired] = useState(false);
  /** Tripjack 2502 duplicate book — show dedicated panel instead of generic error / wrong route. */
  const [duplicateBookingInfo, setDuplicateBookingInfo] = useState(null);
  const pendingFareAcceptanceRef = useRef(null);

  // U2 — Subscribe to supplier session expiry from /review `conditions.st`.
  // `formConditions` is threaded from BookingPage via `bookingData.formConditions`.
  const _sessionExpiry = useSessionExpiry(bookingData?.formConditions || null);
  useEffect(() => {
    if (_sessionExpiry.isExpired && !sessionExpired) {
      setSessionExpired(true);
    }
  }, [_sessionExpiry.isExpired, sessionExpired]);

  const handleFareRepriceModalResult = useCallback((ev) => {
    if (ev?.type === 'still_changed' && ev.detail) {
      setFareRepriceDetail(ev.detail);
      setFareRepriceOpen(true);
      setFareRepriceAttempt((prev) => {
        const fromBackend = ev.detail.reprice_attempt ?? ev.detail.repriceAttempt;
        if (fromBackend != null && fromBackend !== '') {
          const n = Number(fromBackend);
          if (!Number.isNaN(n)) return Math.max(prev, n);
        }
        return prev + 1;
      });
      return;
    }
    if (ev?.type === 'exhausted' && ev.detail) {
      setFareRepriceDetail(ev.detail);
      setFareRepriceOpen(true);
      setRepriceExhausted(true);
      return;
    }
    if (ev?.type === 'ok' && ev.payload) {
      const out = ev.payload;
      const newBid = out.tripjack_booking_id;
      const acceptedTotal = resolveAcceptedNewTotal(out, ev.acceptance, {
        previousMarkedUpTotal: prev?.totalAmount,
      });
      setBookingData((prev) => {
        if (!prev) return prev;
        const prevBid = prev.tripjackBookingId || prev.bookingId
          || (prev.reviewData && (prev.reviewData.bookingId || prev.reviewData.orderId));
        let nextTotal = prev.totalAmount;
        if (acceptedTotal != null) {
          nextTotal = acceptedTotal;
        }
        let passengers = prev.passengers || [];
        if (newBid && prevBid && newBid !== prevBid) {
          passengers = passengers.map((p) => {
            const next = { ...p };
            delete next.ssrSeatInfos;
            return next;
          });
        }
        pendingFareAcceptanceRef.current = {
          ...ev.acceptance,
          user_id: agent?.id || null,
        };
        const sessionBid = tripjackSessionBookingId(out.data, newBid);
        const next = {
          ...prev,
          reviewData: out.data,
          tripjackBookingId: sessionBid,
          bookingId: sessionBid,
          totalAmount: nextTotal,
          passengers,
          ssrSeatStaleAfterReprice: !!(newBid && prevBid && newBid !== prevBid),
        };
        try {
          localStorage.setItem('holdBooking', JSON.stringify(next));
        } catch { /* ignore */ }
        return next;
      });
      setFareRepriceAttempt((prev) => {
        const next = out.reprice_attempt;
        if (next == null || next === '') return prev;
        const n = Number(next);
        if (Number.isNaN(n)) return prev;
        return Math.max(prev, n);
      });
      setFareRepriceOpen(false);
      setFareRepriceDetail(null);
      setError(null);
    }
  }, [agent]);

  useEffect(() => {
    // Get booking data from navigation state or localStorage
    const stateData = location.state?.bookingData;
    const storedData = localStorage.getItem('holdBooking');
    
    let data = null;
    if (stateData) {
      data = stateData;
      localStorage.setItem('holdBooking', JSON.stringify(stateData));
    } else if (storedData) {
      data = JSON.parse(storedData);
      // Defensive — Riya flights cached BEFORE iter34 lack riyaTrackId/riyaBaseAmount/
      // riyaGrossAmount and will fail at /Pricing with cryptic "(&8) TrackId empty"
      // OR Riya sandbox TrackIds expire after ~15 min. Either way, force a re-search.
      if (data?.flightData?.source === 'riya') {
        const stale = !data.flightData.riyaTrackId || !data.flightData.riyaBaseAmount;
        const searchedAtIso = data.flightData.riyaSearchedAt;
        const searchedAtMs = searchedAtIso ? Date.parse(searchedAtIso) : 0;
        const ageMin = searchedAtMs ? (Date.now() - searchedAtMs) / 60000 : Infinity;
        if (stale || ageMin > 14) {
          console.warn(`[riya] Discarding stale localStorage booking (stale=${stale}, ageMin=${ageMin.toFixed(1)})`);
          try {
            localStorage.removeItem('holdBooking');
            localStorage.removeItem('selectedFlight');
          } catch { /* ignore */ }
          setError('Your fare lock has expired. Please re-search this flight and book again.');
          return;
        }
      }
    } else {
      setError('No booking data found. Please start a new booking.');
      return;
    }
    
    // Enrich offline flight data if applicable
    if (data?.flightData?.source === 'offline') {
      console.log('🎫 Enriching offline flight data in ReviewBooking');
      data.flightData = enrichOfflineFlight(data.flightData);
    }
    
    setBookingData(data);
    
    // Fetch payment eligibility for agents
    if (isAgentAuthenticated) {
      fetchPaymentEligibility();
    }
  }, [location, isAgentAuthenticated]);
  
  const fetchPaymentEligibility = async () => {
    try {
      const eligibility = await getPaymentEligibility();
      setPaymentEligibility(eligibility);
      
      // Auto-select the best available payment mode
      if (eligibility) {
        if (eligibility.credit.available) {
          setSelectedPaymentMode('CREDIT');
        } else if (eligibility.wallet.available) {
          setSelectedPaymentMode('WALLET');
        } else {
          setSelectedPaymentMode('GATEWAY');
        }
      }
    } catch (err) {
      console.error('Failed to fetch payment eligibility:', err);
    }
  };
  
  const handleProceedToPayment = async () => {
    if (!bookingData) {
      setError('Booking data not found');
      return;
    }
    // ── B2C OTP gate (Sprint Auth-Hardening Apr-2026 follow-up) ──
    // /api/tripjack/book and /api/riya/book-b2c refuse anonymous callers
    // (see BOOK_ALLOW_ANON guard in tripjack_booking.py:2261). Force any
    // guest to complete phone-OTP login before we open the payment modal.
    // Agents (agent_auth_token cookie) and logged-in B2C users (b2c_auth_token)
    // skip this gate. Offline-flight inventory bookings also skip (they go
    // through a different API surface that doesn't refuse guests).
    const _isOfflineFlight = (bookingData?.flightData?.source || '') === 'offline';
    const isGuest = !isAgentAuthenticated && !isB2CAuthenticated;
    if (isGuest && !_isOfflineFlight) {
      setResumePaymentAfterLogin(true);
      setOtpLoginOpen(true);
      return;
    }
    // U1 + U2 — Hard-block any post-exhaustion / post-expiry continuation.
    if (repriceExhausted || sessionExpired) {
      setError(
        repriceExhausted
          ? 'The fare changed too many times. Please search again to book this flight at the latest price.'
          : 'This flight\'s pricing session has expired. Please search again to refresh prices.',
      );
      return;
    }
    
    setLoading(true);
    setError(null);
    setDuplicateBookingInfo(null);

    try {
      // OFFLINE FLIGHT BOOKING - Special handling
      if (isOfflineFlight) {
        console.log('🎫 Offline flight booking detected');
        
        // For offline flights, we need to book first (reduce inventory) then handle payment
        const offlineBookingPayload = {
          offline_flight_id: bookingData.flightData.id,
          flight_date: bookingData.flightData.flightDate || bookingData.flightData.date,
          passengers: bookingData.passengers.map(p => ({
            firstName: p.fN,
            lastName: p.lN,
            type: p.pt,
            title: p.ti,
            dob: p.dob,
            passportNumber: p.pNum,
            passportExpiry: p.eD,
            passportNationality: p.pNat
          })),
          user_id: agent?.id || null,  // Agent or null for B2C
          email: bookingData.contactEmail,
          phone: bookingData.contactPhone,
          total_amount: bookingData.totalAmount,
          gst_details: bookingData.gstEnabled ? bookingData.gstDetails : null,
          booking_channel: bookingData.flightData?.bookingChannel || null,
        };
        
        // Create offline booking (reduces inventory)
        const offlineBookingResponse = await axios.post(
          `${BACKEND_URL}/api/flights/book-offline`,
          offlineBookingPayload
        );
        
        if (offlineBookingResponse.data.success) {
          const offlineBooking = offlineBookingResponse.data.data;
          console.log('✅ Offline booking created:', offlineBooking);
          
          // For agents with credit/wallet
          if (isAgentAuthenticated && (selectedPaymentMode === 'CREDIT' || selectedPaymentMode === 'WALLET')) {
            // Create agent booking (handles credit/wallet deduction and booking confirmation)
            const agentBookingResponse = await axios.post(
              `${BACKEND_URL}/api/agent/booking/create`,
              {
                payment_mode: selectedPaymentMode,
                bookingType: 'oneway',
                totalAmount: bookingData.totalAmount,
                flightData: bookingData.flightData,
                passengers: bookingData.passengers,
                contactEmail: bookingData.contactEmail,
                contactPhone: bookingData.contactPhone,
                offlineBookingId: offlineBooking.booking_id  // Link to offline booking
              },
              { withCredentials: true, headers: { ...bookingSessionHeaders() } }
            );
            
            // Navigate to success page
            navigate('/booking-success', {
              state: {
                bookingId: offlineBooking.booking_id,
                paymentMode: selectedPaymentMode,
                amountDebited: bookingData.totalAmount,
                remainingBalance: agentBookingResponse.data.remaining_credit || agentBookingResponse.data.remaining_balance,
                isOfflineFlight: true,
                pnr: offlineBooking.pnr,
                bookingDetails: offlineBooking,
                agentBooking: agentBookingResponse.data
              }
            });
          } else {
            // For B2C users or agents using payment gateway
            // Store offline booking ID for post-payment confirmation
            localStorage.setItem('offlineBookingPending', JSON.stringify({
              bookingId: offlineBooking.booking_id,
              pnr: offlineBooking.pnr,
              amount: bookingData.totalAmount
            }));
            
            // Proceed to payment gateway
            await initiatePaymentGateway(bookingData, offlineBooking.booking_id);
          }
        } else {
          setError('Failed to create offline booking. Please try again.');
          setLoading(false);
        }
        
        return;
      }
      
      // AGENT BOOKING FLOW (Tripjack flights)
      if (isAgentAuthenticated && agent) {
        console.log('💼 Agent booking flow with payment mode:', selectedPaymentMode);

        // Sprint May-2026 · Hold Booking — when the user picked "Hold This
        // Fare" on BookingPage, no money is moving yet. Wallet/credit
        // pre-flight checks must be skipped (the hold itself doesn't debit),
        // and the Tripjack /book payload below will OMIT `paymentInfos` so
        // the backend's `is_hold_request = not request.paymentInfos` guard
        // routes to the hold path rather than instant ticketing.
        const isHoldRequest = bookingData.bookingMode === 'hold';

        // Validate selected payment mode eligibility (instant only).
        if (!isHoldRequest && selectedPaymentMode === 'CREDIT') {
          if (!paymentEligibility?.credit?.available) {
            setError('Credit payment is not available. Please select another payment method.');
            setLoading(false);
            return;
          }
          if (paymentEligibility.credit.balance < bookingData.totalAmount) {
            setError(`Insufficient credit. Available: ₹${paymentEligibility.credit.balance}, Required: ₹${bookingData.totalAmount}`);
            setLoading(false);
            return;
          }
        } else if (!isHoldRequest && selectedPaymentMode === 'WALLET') {
          if (!paymentEligibility?.wallet?.available) {
            setError('Wallet payment is not available. Please select another payment method.');
            setLoading(false);
            return;
          }
          if (paymentEligibility.wallet.balance < bookingData.totalAmount) {
            setError(`Insufficient wallet balance. Available: ₹${paymentEligibility.wallet.balance}, Required: ₹${bookingData.totalAmount}`);
            setLoading(false);
            return;
          }
        }

        const isTripjackBooking = isTripjackBookingPayload(bookingData);
        const routeBookingData = isTripjackBooking
          ? {
              ...bookingData,
              flightData: {
                ...(bookingData.flightData || {}),
                source: 'tripjack',
              },
            }
          : bookingData;

        // ── Sprint T3 · Pre-book Instant Fare-Validate ──
        // Eliminates "user paid → 409 FARE_CHANGED" disputes by confirming
        // fare + class one final time before payment capture. Only runs for
        // Tripjack-source bookings that have a bookingId from /review.
        const tjBookingId = tripjackSessionBookingId(
          routeBookingData.reviewData,
          routeBookingData.tripjackBookingId || routeBookingData.bookingId,
        );
        if (tjBookingId && routeBookingData.passengers && routeBookingData.passengers.length) {
          try {
            await axios.post(
              `${BACKEND_URL}/api/tripjack/fare-validate-instant`,
              {
                bookingId: tjBookingId,
                priceIds: routeBookingData.priceIds || [routeBookingData.priceId].filter(Boolean),
                travellerInfo: routeBookingData.passengers,
                deliveryInfo: {
                  emails: [routeBookingData.contactEmail],
                  contacts: [routeBookingData.contactPhone],
                },
                gstInfo: routeBookingData.gstEnabled ? {
                  gstNumber: routeBookingData.gstDetails?.gstNumber,
                  registeredName: routeBookingData.gstDetails?.registeredName,
                  email: routeBookingData.contactEmail,
                  mobile: routeBookingData.gstDetails?.mobile || routeBookingData.contactPhone,
                  address: routeBookingData.gstDetails?.address,
                } : undefined,
              },
              { headers: { ...bookingSessionHeaders() } },
            );
            console.log('✅ Tripjack fare-validate-instant passed');
          } catch (fvErr) {
            const detail = fvErr?.response?.data?.detail;
            const status = fvErr?.response?.status;
            if (status === 409 && detail?.code === 'FARE_CHANGED' && detail?.action === 'REPRICE') {
              setFareRepriceDetail(detail);
              setFareRepriceAttempt(0);
              setFareRepriceOpen(true);
              setLoading(false);
              return;
            }
            if (status === 409 && detail?.code === 'FARE_REPRICE_EXHAUSTED') {
              setFareRepriceDetail(detail);
              setFareRepriceOpen(true);
              setLoading(false);
              return;
            }
            if (status === 409 && detail?.code === 'FARE_CHANGED') {
              const oldF = detail.old_fare ?? '?';
              const newF = detail.new_fare ?? '?';
              const dlt = detail.delta ?? '?';
              setError(
                `The fare has changed since you started booking.\n\n` +
                `Previous fare: ₹${oldF}\nNew fare: ₹${newF}\nDifference: ₹${dlt}\n\n` +
                `Please re-search this flight to see the latest price.`
              );
            } else if (status === 422 && detail?.code === 'FARE_UNAVAILABLE') {
              setError(`This flight is no longer available: ${detail.message || 'class sold out'}. Please re-search.`);
            } else {
              setError(detail?.message || detail || 'Could not confirm fare. Please re-try.');
            }
            setLoading(false);
            return;
          }
        }

        // === Fix · Jul-26 · Route Tripjack+wallet/credit/hold agent bookings to
        // `/api/tripjack/book`. `/api/agent/booking/create` only runs Riya;
        // misrouted Tripjack caused ghost bookings (debited, no /book).
        const useTripjackBookEndpoint =
          isTripjackBooking
          && (isHoldRequest || selectedPaymentMode === 'WALLET' || selectedPaymentMode === 'CREDIT');
        const endpointChosen = useTripjackBookEndpoint ? '/api/tripjack/book' : '/api/agent/booking/create';
        console.log('[BOOKING ROUTE DEBUG]', {
          source: routeBookingData?.flightData?.source,
          supplier: routeBookingData?.flightData?.supplier,
          priceId: routeBookingData?.priceId || routeBookingData?.flightData?.priceId,
          priceIds: routeBookingData?.priceIds,
          onwardPriceId: routeBookingData?.onwardPriceId,
          returnPriceId: routeBookingData?.returnPriceId,
          endpointChosen,
          paymentMode: selectedPaymentMode,
          isTripjackBooking,
        });

        if (useTripjackBookEndpoint) {
          console.log(`✈️ Routing Tripjack agent booking → /api/tripjack/book (mode="${isHoldRequest ? 'hold' : selectedPaymentMode}")`);

          // Build Tripjack /book payload (same shape as PaymentSuccess.jsx)
          // Sprint May-2026 · Hold flow — when bookingMode === 'hold', omit
          // `paymentInfos` so the backend's `is_hold_request = not request.
          // paymentInfos` guard routes to hold ticketing (no wallet debit).
          // The agent will pay later from `/agent/bookings` via the green
          // "Confirm Hold" button (calls `/api/tripjack/confirm-book`).
          const _tjReviewSnap = routeBookingData.reviewData || null;
          const _tjBid = tripjackSessionBookingId(
            _tjReviewSnap,
            routeBookingData.tripjackBookingId || routeBookingData.bookingId,
          );
          const tripjackBookPayload = {
            priceIds: routeBookingData.priceIds || [routeBookingData.priceId].filter(Boolean),
            travellerInfo: routeBookingData.passengers,
            // Tripjack PDF v2.0 · same Air Review session as /seatmap + ancillaries
            ...(_tjReviewSnap && _tjBid
              ? { reviewSnapshot: _tjReviewSnap, tripjackBookingId: _tjBid }
              : {}),
            deliveryInfo: {
              emails: [routeBookingData.contactEmail],
              contacts: [(routeBookingData.contactPhone || '').replace(/^\+?91/, '')],
            },
            ...(isHoldRequest ? {} : {
              paymentInfos: [{
                amount: routeBookingData.totalAmount,
                flight_data: routeBookingData.flightData,  // needed for markup stamping + GST
              }],
            }),
            gstInfo: routeBookingData.gstEnabled && routeBookingData.gstDetails ? {
              gstNumber: routeBookingData.gstDetails.gstNumber,
              registeredName: routeBookingData.gstDetails.registeredName,
              email: routeBookingData.gstDetails.email,
              mobile: routeBookingData.gstDetails.mobile,
              address: routeBookingData.gstDetails.address,
            } : undefined,
            // Sprint-2 · Align agent payload with B2C (PaymentSuccess). These
            // were previously dropped on the agent path, meaning:
            //  • iecr=true fares 422'd even when the user had filled the emergency-contact block
            //  • Baggage-Protection never reached the supplier despite being charged
            //  • STUDENT / SENIOR_CITIZEN fare eligibility wasn't validated
            contactInfo: routeBookingData.emergencyContact ? {
              ecn:      routeBookingData.emergencyContact.name,
              emails:   [routeBookingData.emergencyContact.email].filter(Boolean),
              contacts: [routeBookingData.emergencyContact.phone].filter(Boolean),
            } : null,
            addOnsIds: routeBookingData.addOnsIds || null,
            pft: routeBookingData.pft || null,
            ...(pendingFareAcceptanceRef.current
              ? { fareChangeAcceptance: pendingFareAcceptanceRef.current }
              : {}),
          };

          // Hold bookings don't debit wallet — pass payment_method as `hold`
          // for clearer backend logging + skip the wallet path entirely.
          const _payMethod = isHoldRequest ? 'hold' : (selectedPaymentMode || 'wallet').toLowerCase();
          const tjBookingResponse = await axios.post(
            `${BACKEND_URL}/api/tripjack/book?user_id=${agent.id}&user_type=agent&payment_method=${_payMethod}`,
            tripjackBookPayload,
            { headers: { ...bookingSessionHeaders() } },
          );

          if (tjBookingResponse.data?.success !== false) {
            const data = tjBookingResponse.data || {};
            const booking = data.booking || data;
            const merged = { ...data, ...booking };
            const bkId = getApiBookingRouteId(merged) || (() => {
              const u = getUserFacingBookingId(merged);
              return u !== '—' ? u : (data.bookingId || booking?.booking_id || booking?.bookingId || booking?.id || data.booking_id);
            })();
            const pnr = data.pnr || booking?.pnr;
            pendingFareAcceptanceRef.current = null;
            navigate('/booking-success', {
              state: {
                bookingId: bkId,
                paymentMode: isHoldRequest ? 'HOLD' : selectedPaymentMode,
                // Hold bookings don't debit on creation — show 0 here so
                // the success page renders the "Pay before [hold_until]"
                // CTA instead of the wallet-debited summary.
                amountDebited: isHoldRequest ? 0 : routeBookingData.totalAmount,
                remainingBalance: data.wallet_balance_after || data.remaining_wallet,
                bookingDetails: booking,
                pnr,
                // Surfaced when supplier rejected seat SSR (errCode 1131)
                // and we auto-retried without seats. Booking went through;
                // user just lost their specific seat preference.
                seatWarning: data.seat_warning,
                // Sprint May-2026 · Hold metadata for the success page.
                isHold: isHoldRequest,
                holdUntil: data.hold_until || data.hold_expires_at || data.holdTimeLimit || null,
                holdTimeLimit: data.holdTimeLimit ?? data.hold_until ?? null,
                holdAmount: isHoldRequest ? routeBookingData.totalAmount : 0,
              },
            });
            return;
          }
          setError('Failed to create Tripjack booking. Please try again.');
          setLoading(false);
          return;
        }

        // === Riya / offline → stay on existing agent-booking flow ===
        // Call agent booking API
        const agentBookingPayload = {
          payment_mode: selectedPaymentMode,
          bookingType: routeBookingData.bookingType,
          priceId: routeBookingData.priceId,
          priceIds: routeBookingData.priceIds,
          onwardPriceId: routeBookingData.onwardPriceId,
          returnPriceId: routeBookingData.returnPriceId,
          passengers: routeBookingData.passengers,
          contactEmail: routeBookingData.contactEmail,
          contactPhone: routeBookingData.contactPhone,
          totalAmount: routeBookingData.totalAmount,
          flightData: routeBookingData.flightData,
          gstEnabled: routeBookingData.gstEnabled || false,
          gstDetails: routeBookingData.gstDetails || null,
          ssrSelections: routeBookingData.ssrSelections || null,
          returnSsrSelections: routeBookingData.returnSsrSelections || null,
          addOnsIds: routeBookingData.addOnsIds || null,
          bookingMode: routeBookingData.bookingMode || 'instant',  // Sprint H1+H2
        };
        
        const bookingResponse = await axios.post(
          `${BACKEND_URL}/api/agent/booking/create`,
          agentBookingPayload,
          { headers: { ...bookingSessionHeaders() } },
        );
        
        if (bookingResponse.data.success) {
          console.log('✅ Agent booking created:', bookingResponse.data);
          
          // If payment mode is CREDIT or WALLET, booking is confirmed immediately
          if (selectedPaymentMode === 'CREDIT' || selectedPaymentMode === 'WALLET') {
            // Navigate to success page
            navigate('/booking-success', {
              state: {
                bookingId: bookingResponse.data.booking_id,
                paymentMode: selectedPaymentMode,
                amountDebited: bookingResponse.data.amount_debited,
                remainingBalance: selectedPaymentMode === 'CREDIT' 
                  ? bookingResponse.data.remaining_credit 
                  : bookingResponse.data.remaining_wallet,
                bookingDetails: bookingResponse.data.booking
              }
            });
          } 
          // If payment mode is GATEWAY, redirect to payment gateway
          else if (selectedPaymentMode === 'GATEWAY') {
            // Store booking ID for post-payment processing
            localStorage.setItem('pendingAgentBooking', JSON.stringify({
              bookingId: bookingResponse.data.booking_id,
              amount: bookingData.totalAmount
            }));
            
            // Initiate CCAvenue payment
            await initiatePaymentGateway(bookingData, bookingResponse.data.booking_id);
          }
        } else {
          setError('Failed to create booking. Please try again.');
          setLoading(false);
        }
        
        return;
      }
      
      // B2C USER BOOKING FLOW (Original flow)
      console.log('💳 B2C user booking - Proceeding to payment gateway...');
      console.log('📤 Booking Data:', bookingData);
      
      await initiatePaymentGateway(bookingData);
      
    } catch (err) {
      console.error('Booking error:', err);
      
      let errorMessage = 'Failed to create booking';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail.code === 'PAX_FIELDS_MISSING' && Array.isArray(detail.errors)) {
          const lines = detail.errors.map(e => `• ${e.message || `${e.pax_type} pax #${(e.pax_index ?? 0) + 1}: ${e.field}`}`);
          errorMessage = `${detail.message || 'This fare requires additional passenger information.'}\n\n${lines.join('\n')}`;
        } else if (detail.code === 'FARE_CHANGED') {
          errorMessage =
            `${detail.message || 'The fare has changed since you started booking.'}\n\n` +
            `Old fare: ₹${Number(detail.old_fare || 0).toLocaleString('en-IN')}\n` +
            `New fare: ₹${Number(detail.new_fare || 0).toLocaleString('en-IN')}\n\n` +
            `Please go back and re-search to see the current fare.`;
        } else if (
          detail.code === 'INVALID_EMAIL' || detail.code === 'INVALID_PHONE' ||
          detail.code === 'INVALID_PAX_NAME' || detail.code === 'INVALID_DOB' ||
          detail.code === 'INVALID_PASSPORT'
        ) {
          // Field-level validation rejected by airline — show clear actionable message.
          const fieldLabel = {
            INVALID_EMAIL: 'Email',
            INVALID_PHONE: 'Phone',
            INVALID_PAX_NAME: 'Passenger name',
            INVALID_DOB: 'Date of birth',
            INVALID_PASSPORT: 'Passport',
          }[detail.code] || 'Field';
          errorMessage = `${fieldLabel}: ${detail.message || 'Invalid format'}`;
        } else if (detail.code === 'SEATS_UNAVAILABLE') {
          errorMessage = detail.message || 'Seats no longer available. Please re-search.';
        } else if (detail.action === 'lookup_existing' || Number(detail.code) === 2502) {
          const acfId = detail.existing_booking_id || detail.acf_booking_id;
          // Use mapped user_message only — never raw supplier_message (may expose partner ids).
          setDuplicateBookingInfo({
            message: detail.message || undefined,
            acfBookingId: acfId,
          });
          setError('');
          setLoading(false);
          return;
        } else if (detail.action === 'rebook' || [1000, 1009, 1059, 1071, 1072].includes(Number(detail.code))) {
          // Tripjack supplier inventory/fare-state rejection — fare expired,
          // flight withdrawn, fare jump, etc. Always non-recoverable on the
          // current priceId; user must re-search.
          errorMessage = detail.message || 'This flight is no longer available. Please search again.';
          const ref2 = detail.supplier_booking_ref;
          if (ref2 && !String(ref2).trim().startsWith('TJ')) {
            errorMessage += `\n\nReference: ${ref2}`;
          }
          // Purge stale cached booking data
          try {
            localStorage.removeItem('holdBooking');
            localStorage.removeItem('selectedFlight');
            localStorage.removeItem('pendingBooking');
            // Sprint-4 · also drop the server-side payment session
            const tok = sessionStorage.getItem('paymentSessionToken');
            if (tok) {
              sessionStorage.removeItem('paymentSessionToken');
              axios.delete(`${BACKEND_URL}/api/payment/session/${encodeURIComponent(tok)}`).catch(() => {});
            }
          } catch { /* ignore */ }
          // Auto-redirect to fresh search
          const fd = bookingData?.flightData || {};
          const from = fd.from || fd.route?.from || '';
          const to = fd.to || fd.route?.to || '';
          const date = fd.date || fd.flightDate || fd.departureDate || '';
          // Sprint-3 · Rebook redirect with REAL pax counts (was always 1
          // because `bookingData.adults` is undefined; counts live on
          // `passengersRaw`). Counting by passenger type avoids dropping
          // children/infants on the re-search.
          const _paxList = bookingData?.passengersRaw || bookingData?.passengers || [];
          const _adults = _paxList.filter(p => (p.type || p.pt || 'ADULT').toUpperCase() === 'ADULT').length || 1;
          const _children = _paxList.filter(p => (p.type || p.pt || '').toUpperCase() === 'CHILD').length;
          const _infants = _paxList.filter(p => (p.type || p.pt || '').toUpperCase() === 'INFANT').length;
          if (from && to && date) {
            setTimeout(() => {
              navigate(`/search?from=${from}&to=${to}&date=${date}&adults=${_adults}&children=${_children}&infants=${_infants}&class=Economy&type=oneWay`);
            }, 2500);
          }
        } else if (detail.code === 'RIYA_SEARCH_EXPIRED' || detail.code === 'RIYA_TOKEN_EXPIRED') {
          errorMessage = detail.message || 'Your fare lock has expired. Please re-search this flight.';
          // Purge stale cached booking data so the user can't retry the same expired payload.
          try {
            localStorage.removeItem('holdBooking');
            localStorage.removeItem('selectedFlight');
            localStorage.removeItem('pendingBooking');
            const tok2 = sessionStorage.getItem('paymentSessionToken');
            if (tok2) {
              sessionStorage.removeItem('paymentSessionToken');
              axios.delete(`${BACKEND_URL}/api/payment/session/${encodeURIComponent(tok2)}`).catch(() => {});
            }
          } catch { /* ignore */ }
          // Auto-redirect to fresh search after a short delay so the user sees the message.
          const fd = bookingData?.flightData || {};
          const from = fd.from || fd.route?.from || '';
          const to = fd.to || fd.route?.to || '';
          const date = fd.date || fd.flightDate || fd.departureDate || '';
          // Sprint-3 · Rebook redirect with REAL pax counts (mirror of above).
          const _paxList2 = bookingData?.passengersRaw || bookingData?.passengers || [];
          const _adults2 = _paxList2.filter(p => (p.type || p.pt || 'ADULT').toUpperCase() === 'ADULT').length || 1;
          const _children2 = _paxList2.filter(p => (p.type || p.pt || '').toUpperCase() === 'CHILD').length;
          const _infants2 = _paxList2.filter(p => (p.type || p.pt || '').toUpperCase() === 'INFANT').length;
          if (from && to && date) {
            setTimeout(() => {
              navigate(`/search?from=${from}&to=${to}&date=${date}&adults=${_adults2}&children=${_children2}&infants=${_infants2}&class=Economy&type=oneWay`);
            }, 2500);
          }
        } else {
          errorMessage = detail.message || JSON.stringify(detail);
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };
  
  const initiatePaymentGateway = async (bookingData, bookingId = null) => {
    // Generate unique order ID
    const orderId = bookingId || `ORD${Date.now()}${Math.floor(Math.random() * 1000)}`;

    // Get passenger name (first passenger as billing name)
    const firstPassenger = bookingData.passengers[0];
    const billingName = `${firstPassenger.ti} ${firstPassenger.fN} ${firstPassenger.lN}`;

    // Sprint-2 · Billing address from user profile / GST / agent — in
    // priority order. Previously every CCAvenue call sent the same
    // hardcoded Mumbai address for every user, which fails risk checks on
    // some merchants and misrepresents KYC.
    //   1. GST details (most accurate when the user provided them)
    //   2. Agent profile (billing_address + state from signup)
    //   3. Conservative default (still India, but clearly a placeholder)
    const gst = bookingData.gstDetails || null;
    const gstEnabled = !!bookingData.gstEnabled;

    let billing_address = '';
    let billing_city = '';
    let billing_state = '';
    let billing_zip = '';
    if (gstEnabled && gst && gst.address) {
      billing_address = gst.address || '';
      billing_city = gst.city || '';
      billing_state = gst.state || '';
      billing_zip = gst.pincode || gst.zip || '';
    } else if (agent) {
      billing_address = agent.billing_address || '';
      billing_state = agent.state || '';
      // Agent city/zip aren't captured at signup → leave blank; CCAvenue
      // accepts partial address when `billing_address` + state are set.
    }

    // Final fallback — keep the booking going, but flag to backend logs.
    if (!billing_address) {
      billing_address = 'India';
    }
    if (!billing_city) billing_city = billing_state || 'NA';
    if (!billing_state) billing_state = 'NA';
    if (!billing_zip) billing_zip = '000000';

    // Prepare payment payload
    const paymentPayload = {
      order_id: orderId,
      amount: bookingData.totalAmount,
      currency: 'INR',
      billing_name: billingName,
      billing_email: bookingData.contactEmail,
      billing_tel: bookingData.contactPhone,
      billing_address,
      billing_city,
      billing_state,
      billing_zip,
      billing_country: 'India',
      customer_name: billingName
    };
    
    // Sprint-4 (Feb 2026) · DPDP-compliance: store the booking payload
    // server-side, NOT in localStorage. We only stash the opaque
    // session_token in sessionStorage (no PII leaves the user's device
    // in cleartext). Token is also embedded in CCAvenue's redirect URL
    // hash so a fresh tab can recover the session.
    const paymentData = {
      ...bookingData,
      orderId: orderId,
      timestamp: new Date().toISOString()
    };

    let sessionToken = null;
    try {
      const sessionResp = await axios.post(
        `${BACKEND_URL}/api/payment/session`,
        paymentData
      );
      sessionToken = sessionResp.data.session_token;
      sessionStorage.setItem('paymentSessionToken', sessionToken);
      console.log('🔒 Stored booking payload server-side; token in sessionStorage');
    } catch (sessErr) {
      console.error('⚠️ Payment session creation failed, falling back to localStorage:', sessErr);
      // Fallback so a backend hiccup doesn't block bookings outright;
      // the cleanup happens in PaymentSuccess regardless of which path
      // populated the data.
      localStorage.setItem('pendingBooking', JSON.stringify(paymentData));
    }
    
    console.log('🔍 Payment amount:', paymentPayload.amount);

    await openRazorpayCheckout({
      createOrderPayload: {
        context: 'flight',
        receipt: orderId,
        amount: Number(paymentPayload.amount),
        currency: 'INR',
        agent_id: agent?.id || null,
        metadata: { session_token: sessionToken },
      },
      prefill: {
        name: billingName,
        email: bookingData.contactEmail,
        contact: bookingData.contactPhone,
      },
      description: 'Flight booking payment',
      onVerified: (data) => {
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        }
      },
    });
  };
  
  const handleEditDetails = () => {
    // Navigate back to booking page with current data
    navigate(-1);
  };
  
  if (error) {
    // Distinguish "no booking data found" (terminal — must restart) from
    // "submit-time" errors (user CAN fix and retry). For submit-time errors we
    // keep them on /review-booking with an inline alert + Edit/Retry CTAs.
    const isTerminal = !bookingData || /no booking data/i.test(error);
    if (isTerminal) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8 max-w-md w-full text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-600 mb-6 whitespace-pre-line text-left" data-testid="review-booking-error">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-[#1B375B] text-white rounded-lg font-bold hover:bg-[#335E77]"
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }
    // Non-terminal error → inline banner on the review page; user keeps form.
    // (rendered inline below, see banner block before the Confirm CTA)
  }
  
  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  const { flightData, passengers, contactEmail, contactPhone, totalAmount } = bookingData;

  const commissionFare = deriveCommissionFare({
    amountToPay: totalAmount,
    commissionPreview: resolveCommissionPreview(
      { commission_preview: bookingData.commissionPreview },
      bookingData,
      agent,
    ),
  });
  
  // Debug: Log flight data structure
  console.log('📋 Review Page - Flight Data:', flightData);
  console.log('📋 Review Page - Full Booking Data:', bookingData);
  
  // Check if this is an offline flight
  const isOfflineFlight = flightData?.source === 'offline';
  
  // Extract flight details with fallbacks for different data structures
  const getFlightDetails = () => {
    // Handle different flightData structures
    if (!flightData) return null;
    
    // Check if it's a direct flight object or nested
    const flight = flightData.flight || flightData;
    
    // Extract date with multiple fallbacks
    const getDepartureDate = () => {
      // For offline flights, use flightDate
      if (isOfflineFlight && flightData.flightDate) {
        return new Date(flightData.flightDate).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      }
      
      // Try various date fields
      const date = flight.departureDate || 
                   flightData.route?.departureDate || 
                   flightData.date || 
                   flightData.departureDate ||
                   flight.date;
      
      if (date) return date;
      
      // If we have a timestamp, format it
      if (bookingData.timestamp) {
        return new Date(bookingData.timestamp).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      }
      
      return 'N/A';
    };
    
    return {
      airline: flightData.airline || flight.airline || flightData.airlineName || 'Airline',
      flightNumber: flightData.flightNumber || flight.flightNumber || flight.flightNo || 'N/A',
      source: flight.source || flight.from || flightData.route?.from || flightData.from,
      destination: flight.destination || flight.to || flightData.route?.to || flightData.to,
      sourceCity: isOfflineFlight ? flightData.fromCity : (flight.sourceCity || ''),
      destinationCity: isOfflineFlight ? flightData.toCity : (flight.destinationCity || ''),
      departureTime: flight.departureTime || flight.depTime || flightData.departureTime,
      arrivalTime: flight.arrivalTime || flight.arrTime || flightData.arrivalTime,
      departureDate: getDepartureDate(),
      arrivalDate: flight.arrivalDate || getDepartureDate(),
      duration: flight.duration || flightData.duration || 'N/A',
      stops: flightData.stops !== undefined ? flightData.stops : (flight.stops || 0),
      // Sprint-7 F2 · No hardcoded baggage defaults — surface only what the
      // supplier actually returned. Render layer below hides rows on null.
      baggage: flightData.baggage || flightData.checkinBaggage || flight.baggage || null,
      cabinBaggage: isOfflineFlight ? flightData.cabinBaggage : (flight.cabinBaggage || null),
      class: flightData.class || flightData.classOfBooking || flight.class || 'Economy',
      fareType: isOfflineFlight ? flightData.fareType : (flight.fareType || 'Regular'),
      pnr: isOfflineFlight ? flightData.pnr : null,
      seatsLeft: isOfflineFlight ? flightData.seatsLeft : null,
      mealIncluded: isOfflineFlight ? flightData.mealIncluded : false,
      refundable: isOfflineFlight ? flightData.refundable : false
    };
  };
  
  const flightDetails = getFlightDetails();
  
  // Extract SSR selections if available
  const ssrSelections = bookingData.ssrSelections || {};
  const hasAddons = bookingData.addOnsIds || 
                    Object.keys(ssrSelections.baggage || {}).length > 0 || 
                    Object.keys(ssrSelections.meals || {}).length > 0 || 
                    Object.keys(ssrSelections.seats || {}).length > 0;
  
  // Calculate add-ons summary
  const getAddonsSummary = () => {
    let total = 0;
    let count = 0;
    
    // Baggage protection
    if (bookingData.addOnsIds?.bp) {
      total += bookingData.addOnsIds.bp;
      count += 1;
    }
    
    // Extra baggage
    if (ssrSelections.baggage) {
      Object.values(ssrSelections.baggage).forEach(bag => {
        if (bag) {
          total += bag.amount;
          count += 1;
        }
      });
    }
    
    // Meals
    if (ssrSelections.meals) {
      Object.values(ssrSelections.meals).forEach(meal => {
        if (meal) {
          total += meal.amount;
          count += 1;
        }
      });
    }
    
    // Seats
    if (ssrSelections.seats) {
      Object.values(ssrSelections.seats).forEach(seat => {
        if (seat) {
          total += seat.amount;
          count += 1;
        }
      });
    }
    
    return { total, count };
  };
  
  const addonsSummary = getAddonsSummary();

  // Sprint May-2026 · Single source of truth for hold-mode UI gating.
  // The user picked "Hold This Fare" on BookingPage; here on /review-booking
  // we suppress the Payment Method selector entirely (no money is moving),
  // skip wallet/credit pre-flight checks, and re-label the CTA so the
  // agent isn't confused into thinking they have to choose a payment.
  const isHoldRequest = bookingData?.bookingMode === 'hold';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 pb-32 md:pb-8">
      {/* Mobile Only Navbar */}
      <div className="md:hidden">
        <Navbar />
      </div>
      
      {/* Mobile Only Page Header */}
      <div className="md:hidden">
        <BookingPageHeader 
          title="Review Booking"
          subtitle="Please review your details before proceeding to payment"
          step={3}
          totalSteps={3}
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Desktop: 2-Column Grid | Mobile: Single Column */}
        <div className="md:grid md:grid-cols-[2fr_1fr] md:gap-6 md:items-start">
          {/* LEFT COLUMN: Main Content */}
          <div className="space-y-6">
            {/* Offline Flight Badge */}
            {isOfflineFlight && (
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 rounded-lg shadow-md flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Luggage className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">Block Ticket - Offline Inventory</p>
                  {flightDetails?.pnr && (
                    <p className="text-xs text-white/90 mt-0.5">
                      PNR: <span className="font-mono font-semibold">{flightDetails.pnr}</span>
                    </p>
                  )}
                </div>
                {flightDetails?.seatsLeft && flightDetails.seatsLeft < 5 && (
                  <div className="bg-white/20 px-3 py-1 rounded-full">
                    <p className="text-xs font-bold">{flightDetails.seatsLeft} seats left</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Flight Details Card - Consistent with other pages */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3 md:hidden">Flight Details</h3>
              <FlightDetailsCard 
                flightData={bookingData?.flightData}
                displayFlight={bookingData?.flightData?.flight || bookingData?.flightData}
              />
            </div>

            {/* Detailed Fare Rules — Tripjack flights only (priceId required). */}
            {/* Offline + Riya flights have their own rules surfaces upstream. */}
            {(!isOfflineFlight
              && bookingData?.flightData?.source !== 'riya'
              && (bookingData?.priceId || bookingData?.priceIds?.length)) && (
              <FareRulesPanel
                priceId={bookingData?.priceId}
                priceIds={bookingData?.priceIds}
                bookingId={bookingData?.tripjackBookingId || bookingData?.bookingId}
                flowType="REVIEW"
                embeddedNormalizedFareRules={bookingData?.embeddedFareRulesNormalized ?? null}
              />
            )}
        
        {/* Enhanced Passenger Details Card */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-md overflow-hidden border border-blue-100">
          <div className="bg-gradient-to-r from-[#1B375B] to-[#335E77] px-6 py-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Passenger Details
            </h2>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
              {passengers.map((passenger, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 hover:border-[#659A9B] transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#659A9B] to-[#335E77] flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          {passenger.pt === 'ADULT' ? 'Adult' : passenger.pt === 'CHILD' ? 'Child' : 'Infant'}
                        </span>
                        <p className="font-bold text-[#1B375B] text-lg leading-tight">
                          {passenger.ti} {passenger.fN} {passenger.lN}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {(passenger.dob || passenger.pNum) && (
                    <div className="space-y-2 mt-3 pt-3 border-t border-gray-100">
                      {passenger.dob && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">DOB:</span>
                          <span className="font-medium text-gray-800">{passenger.dob}</span>
                        </div>
                      )}
                      {passenger.pNum && (
                        <div className="flex items-center gap-2 text-sm">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          <span className="text-gray-600">Passport:</span>
                          <span className="font-medium text-gray-800">{passenger.pNum}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Enhanced Contact Details Card - Theme Colors */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-md overflow-hidden border border-blue-100">
          <div className="bg-gradient-to-r from-[#659A9B] to-[#335E77] px-6 py-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Contact Information
            </h2>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 flex items-center gap-2 sm:gap-3 hover:border-[#659A9B] transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#659A9B]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#659A9B]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Email Address</p>
                  <p className="font-semibold text-gray-800 truncate">{contactEmail}</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 flex items-center gap-2 sm:gap-3 hover:border-[#659A9B] transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#659A9B]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#659A9B]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Phone Number</p>
                  <p className="font-semibold text-gray-800">{contactPhone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
          {/* End of LEFT COLUMN */}

          {/* RIGHT COLUMN: Sticky Price Breakdown (Desktop Only) */}
          <div className="hidden md:block">
            <div className="sticky top-6 max-w-sm mx-auto">
              {/* Enhanced Price Summary Card - Theme Colors */}
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg overflow-hidden border-2 border-[#659A9B]">
          <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Price Breakdown
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {/* Sprint Feb-2026 · Pax-aware Price Breakdown (SSOT: deriveDisplayFare). */}
              {(() => {
                const _paxList = bookingData?.passengers || bookingData?.passengersRaw || [];
                const derived = derivePaxCountsFromPassengers(_paxList);
                const numA =
                  derived.adults > 0
                    ? derived.adults
                    : Number(flightData?._paxCount?.adults || flightData?.adults || 1);
                const numC =
                  derived.children > 0
                    ? derived.children
                    : Number(flightData?._paxCount?.children || flightData?.children || 0);
                const numI =
                  derived.infants > 0
                    ? derived.infants
                    : Number(flightData?._paxCount?.infants || flightData?.infants || 0);
                const bt =
                  bookingData?.bookingType || flightData?.type || flightData?.bookingType || 'oneway';
                const fd = deriveDisplayFare({
                  flightData,
                  bookingType: bt,
                  numAdults: numA,
                  numChildren: numC,
                  numInfants: numI,
                  supplierTotalOverride: bookingData?.totalAmount,
                });
                const _bf = fd.baseFare;
                const _taf = fd.taxes;
                const _mu = fd.serviceCharge;
                const _paxLine = fd.paxLine;
                const _muSubLine = fd.muSubLine;
                const showBreakdown = (_bf + _taf) > 0;
                if (!showBreakdown) {
                  return (
                    <div className="text-xs text-gray-500" data-testid="review-fare-no-breakdown-note">
                      Itemised fare breakdown will appear on your GST invoice.
                    </div>
                  );
                }
                return (
                  <>
                    {_paxLine && (
                      <div className="text-xs text-gray-500 pb-2 border-b border-gray-100" data-testid="review-pax-summary">
                        <span className="font-medium text-gray-700">Travelers:</span> {_paxLine}
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2" data-testid="review-base-fare-row">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Plane className="w-4 h-4 text-gray-400" />
                        Base Fare
                      </span>
                      <span className="font-semibold text-gray-800">₹{Math.round(_bf).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2" data-testid="review-taxes-row">
                      <span className="text-gray-600 flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Taxes & Fees
                      </span>
                      <span className="font-semibold text-gray-800">₹{Math.round(_taf).toLocaleString()}</span>
                    </div>
                    {_mu > 0 && (
                      <div className="py-2" data-testid="review-service-charge-row">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Service Charge
                          </span>
                          <span className="font-semibold text-gray-800">₹{Math.round(_mu).toLocaleString()}</span>
                        </div>
                        {_muSubLine && (
                          <p className="text-[11px] text-gray-500 ml-6 mt-0.5" data-testid="review-service-charge-breakdown">
                            {_muSubLine}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
              
              {/* Offline Flight Info */}
              {isOfflineFlight && (
                <div className="border-t border-gray-200 my-3 pt-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-bold text-amber-800 uppercase flex items-center gap-1">
                      <Luggage className="w-3 h-3" />
                      Block Ticket Details
                    </p>
                    {flightDetails?.mealIncluded && (
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Check className="w-3 h-3 text-green-600" />
                        <span>Meal Included</span>
                      </div>
                    )}
                    {flightDetails?.refundable ? (
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Check className="w-3 h-3 text-green-600" />
                        <span>Refundable</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <X className="w-3 h-3 text-red-600" />
                        <span>Non-Refundable</span>
                      </div>
                    )}
                    {/* Sprint-7 F2 · Hide baggage rows when supplier did not surface values. */}
                    {flightDetails?.baggage && (
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Luggage className="w-3 h-3" />
                        <span>Check-in: {flightDetails.baggage}</span>
                      </div>
                    )}
                    {flightDetails?.cabinBaggage && (
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Luggage className="w-3 h-3" />
                        <span>Cabin: {flightDetails.cabinBaggage}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            
            {/* Add-ons Section - Collapsible - Theme Colors */}
            {hasAddons && addonsSummary.count > 0 && (
              <>
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Add-ons Summary (Clickable) */}
                <button
                  onClick={() => setAddonsExpanded(!addonsExpanded)}
                  className="w-full flex justify-between items-center py-3 px-4 bg-gradient-to-r from-[#659A9B]/10 to-[#335E77]/10 hover:from-[#659A9B]/20 hover:to-[#335E77]/20 rounded-lg transition-all group border border-[#659A9B]/30"
                >
                  <div className="flex items-center gap-2">
                    {addonsExpanded ? (
                      <ChevronUp className="w-4 h-4 text-[#659A9B]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#659A9B]" />
                    )}
                    <Luggage className="w-4 h-4 text-[#659A9B]" />
                    <span className="text-gray-700 font-semibold">
                      Add-ons ({addonsSummary.count} {addonsSummary.count === 1 ? 'item' : 'items'})
                    </span>
                  </div>
                  <span className="font-bold text-[#659A9B]">+₹{addonsSummary.total.toLocaleString()}</span>
                </button>
                
                {/* Expanded Add-ons Details */}
                {addonsExpanded && (
                  <div className="ml-6 space-y-2 mt-2 pb-2">
                    {/* Baggage Protection */}
                    {bookingData.addOnsIds?.bp && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">• Baggage Protection</span>
                        <span className="font-medium text-[#659A9B]">+₹{bookingData.addOnsIds.bp}</span>
                      </div>
                    )}
                    
                    {/* Extra Baggage */}
                    {ssrSelections.baggage && Object.entries(ssrSelections.baggage).map(([idx, baggage]) => {
                      if (!baggage) return null;
                      const passenger = bookingData.passengersRaw?.[idx] || passengers[idx];
                      return (
                        <div key={`bag-${idx}`} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            • Extra Baggage ({passenger?.firstName || passenger?.fN} - {baggage.desc})
                          </span>
                          <span className="font-medium text-[#659A9B]">+₹{baggage.amount.toLocaleString()}</span>
                        </div>
                      );
                    })}
                    
                    {/* Meals */}
                    {ssrSelections.meals && Object.entries(ssrSelections.meals).map(([idx, meal]) => {
                      if (!meal) return null;
                      const passenger = bookingData.passengersRaw?.[idx] || passengers[idx];
                      return (
                        <div key={`meal-${idx}`} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            • Meal ({passenger?.firstName || passenger?.fN} - {meal.desc})
                          </span>
                          <span className="font-medium text-[#659A9B]">+₹{meal.amount.toLocaleString()}</span>
                        </div>
                      );
                    })}
                    
                    {/* Seats */}
                    {ssrSelections.seats && Object.entries(ssrSelections.seats).map(([idx, seat]) => {
                      if (!seat) return null;
                      const passenger = bookingData.passengersRaw?.[idx] || passengers[idx];
                      return (
                        <div key={`seat-${idx}`} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            • Seat ({passenger?.firstName || passenger?.fN} - {seat.code})
                          </span>
                          <span className="font-medium text-[#659A9B]">+₹{seat.amount.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            
            {/* Enhanced Total Section - Theme Colors */}
            <div className="mt-4 pt-4 border-t-2 border-[#659A9B]/30">
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] rounded-lg p-3 sm:p-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-white/80 mb-1">
                    {commissionFare.showAmountToPayLabel ? 'Amount to Pay' : 'Total Amount'}
                  </p>
                  <p className="text-3xl font-black text-white">₹{totalAmount.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/80">Inclusive of all taxes</p>
                  <p className="text-sm text-white font-semibold">Safe & Secure Payment</p>
                </div>
              </div>
              <FareCommissionRows commissionFare={commissionFare} />
            </div>

            {/* Sprint May-2026 · Hold-mode notice — replaces the payment
                method selector entirely. No payment is collected at hold
                time; agent confirms later from /agent/bookings. */}
            {isAgentAuthenticated && isHoldRequest && (
              <div
                className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl"
                data-testid="review-hold-notice"
              >
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-bold text-amber-900 mb-1">
                      Hold mode — no payment due now
                    </h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      You're placing this booking on hold. The airline will
                      reserve your seats for ~8 hours. <strong>No wallet,
                      credit, or card charge happens now.</strong> Confirm and
                      pay later from <em>My Bookings → Confirm Hold</em>{' '}
                      before the timer expires, or the seats auto-release.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Agent Payment Mode Selection */}
            {isAgentAuthenticated && !isHoldRequest && paymentEligibility && (
              <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl border border-[#659A9B]/30">
                <h3 className="text-lg font-bold text-[#1B375B] mb-3 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-[#659A9B]" />
                  Select Payment Method
                </h3>
                
                <div className="space-y-3">
                  {/* Credit Option */}
                  <div
                    className={`relative p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedPaymentMode === 'CREDIT'
                        ? 'border-[#659A9B] bg-white shadow-md'
                        : paymentEligibility.credit.available
                        ? 'border-gray-300 bg-white hover:border-[#659A9B]/50'
                        : 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                    }`}
                    onClick={() => paymentEligibility.credit.available && setSelectedPaymentMode('CREDIT')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMode"
                          value="CREDIT"
                          checked={selectedPaymentMode === 'CREDIT'}
                          onChange={() => setSelectedPaymentMode('CREDIT')}
                          disabled={!paymentEligibility.credit.available}
                          className="w-4 h-4 text-[#659A9B] focus:ring-[#659A9B]"
                        />
                        <div>
                          <p className="font-semibold text-gray-800">Credit Limit</p>
                          <p className="text-xs text-gray-600">
                            Available: ₹{paymentEligibility.credit.balance?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                      {paymentEligibility.credit.available && (
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Available</span>
                      )}
                    </div>
                    {!paymentEligibility.credit.available && paymentEligibility.credit.reason && (
                      <p className="text-xs text-red-600 mt-2 ml-7">{paymentEligibility.credit.reason}</p>
                    )}
                  </div>

                  {/* Wallet Option */}
                  <div
                    className={`relative p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedPaymentMode === 'WALLET'
                        ? 'border-[#659A9B] bg-white shadow-md'
                        : paymentEligibility.wallet.available
                        ? 'border-gray-300 bg-white hover:border-[#659A9B]/50'
                        : 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                    }`}
                    onClick={() => paymentEligibility.wallet.available && setSelectedPaymentMode('WALLET')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMode"
                          value="WALLET"
                          checked={selectedPaymentMode === 'WALLET'}
                          onChange={() => setSelectedPaymentMode('WALLET')}
                          disabled={!paymentEligibility.wallet.available}
                          className="w-4 h-4 text-[#659A9B] focus:ring-[#659A9B]"
                        />
                        <div>
                          <p className="font-semibold text-gray-800">Cash Wallet</p>
                          <p className="text-xs text-gray-600">
                            Balance: ₹{paymentEligibility.wallet.balance?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                      {paymentEligibility.wallet.available && (
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Available</span>
                      )}
                    </div>
                    {!paymentEligibility.wallet.available && paymentEligibility.wallet.reason && (
                      <p className="text-xs text-red-600 mt-2 ml-7">{paymentEligibility.wallet.reason}</p>
                    )}
                  </div>

                  {/* Payment Gateway Option */}
                  <div
                    className={`relative p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedPaymentMode === 'GATEWAY'
                        ? 'border-[#659A9B] bg-white shadow-md'
                        : 'border-gray-300 bg-white hover:border-[#659A9B]/50'
                    }`}
                    onClick={() => setSelectedPaymentMode('GATEWAY')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMode"
                          value="GATEWAY"
                          checked={selectedPaymentMode === 'GATEWAY'}
                          onChange={() => setSelectedPaymentMode('GATEWAY')}
                          className="w-4 h-4 text-[#659A9B] focus:ring-[#659A9B]"
                        />
                        <div>
                          <p className="font-semibold text-gray-800">Payment Gateway</p>
                          <p className="text-xs text-gray-600">Pay via Razorpay (Card/UPI/NetBanking)</p>
                        </div>
                      </div>
                      <CreditCard className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Payment Info Note */}
                <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      {selectedPaymentMode === 'CREDIT' && 'Amount will be deducted from your credit limit immediately.'}
                      {selectedPaymentMode === 'WALLET' && 'Amount will be deducted from your cash wallet balance immediately.'}
                      {selectedPaymentMode === 'GATEWAY' && 'You will be redirected to secure payment gateway.'}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {duplicateBookingInfo ? (
              <div className="mt-4 mb-2" data-testid="review-duplicate-booking">
                <DuplicateBookingPanel
                  message={duplicateBookingInfo.message}
                  acfBookingId={duplicateBookingInfo.acfBookingId}
                  isAgent={!!isAgentAuthenticated}
                  onViewBooking={
                    duplicateBookingInfo.acfBookingId
                      ? () => navigate(`/booking-details/${encodeURIComponent(duplicateBookingInfo.acfBookingId)}`)
                      : undefined
                  }
                  onGoBookings={() => navigate(isAgentAuthenticated ? '/agent/bookings' : '/user/dashboard/bookings')}
                  onGoHome={() => navigate('/')}
                />
                <button
                  type="button"
                  onClick={() => setDuplicateBookingInfo(null)}
                  className="mt-3 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  Dismiss
                </button>
              </div>
            ) : null}

            {/* Inline error banner — shows AFTER a failed submit (booking.create rejection)
                so the user can fix the offending field and retry without losing form state.
                Uses `sanitizeBookingError` to strip raw server internals (BSON / stack traces)
                so we never leak Mongo/Python errors into the UI, and we hide the
                "Edit Passenger Details" CTA when fixing the form can't help. */}
            {error && (() => {
              const { userMessage, isInternal } = sanitizeBookingError(error);
              return (
              <div
                role="alert"
                data-testid="review-inline-error"
                className="mt-4 mb-2 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800 mb-1">Could not complete booking</p>
                  <p className="text-sm text-red-700 whitespace-pre-line" data-testid="review-inline-error-message">{userMessage}</p>
                  <div className="mt-3 flex gap-2">
                    {!isInternal && (
                      <button
                        onClick={handleEditDetails}
                        className="px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-md text-xs font-semibold hover:bg-red-50"
                        data-testid="review-error-edit-btn"
                      >
                        Edit Passenger Details
                      </button>
                    )}
                    {isInternal && (
                      <button
                        onClick={() => window.location.reload()}
                        className="px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-md text-xs font-semibold hover:bg-red-50"
                        data-testid="review-error-retry-btn"
                      >
                        Retry
                      </button>
                    )}
                    <button
                      onClick={() => setError(null)}
                      className="px-3 py-1.5 text-red-700 text-xs font-semibold hover:underline"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Desktop Only Action Button */}
            <div className="mt-6 space-y-2">
              {/* U1 + U2 — Session-expiry chip + exhausted banner above the desktop Pay CTA. */}
              <ReviewMessagesBanner
                formConditions={bookingData?.formConditions}
                testIdSuffix="review-booking-desktop"
              />
              {(_sessionExpiry.hasSupplierTtl || repriceExhausted) && (
                <div className="flex flex-col gap-2">
                  {_sessionExpiry.hasSupplierTtl && (
                    <SessionExpiryChip
                      formConditions={bookingData?.formConditions || null}
                      onExpire={() => setSessionExpired(true)}
                      testIdSuffix="review-booking-desktop"
                    />
                  )}
                  {repriceExhausted && (
                    <div
                      className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800"
                      data-testid="reprice-exhausted-banner-review-desktop"
                      role="alert"
                    >
                      <span className="font-semibold">Fare locked.</span>{' '}
                      The fare changed too many times. Please search again to book at the latest price.
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                data-testid={
                  isHoldRequest
                    ? 'review-place-hold-btn'
                    : isAgentAuthenticated && selectedPaymentMode === 'CREDIT'
                      ? 'review-pay-credit-btn'
                      : isAgentAuthenticated && selectedPaymentMode === 'WALLET'
                        ? 'review-pay-wallet-btn'
                        : 'review-pay-razorpay-btn'
                }
                onClick={handleProceedToPayment}
                disabled={loading || repriceExhausted || sessionExpired}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 shadow-md"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (repriceExhausted || sessionExpired) ? (
                  <>
                    <AlertTriangle className="w-5 h-5" />
                    <span>Please re-search to continue</span>
                  </>
                ) : (
                  <>
                    {isAgentAuthenticated ? (
                      <>
                        {isHoldRequest && (
                          <>
                            <Clock className="w-5 h-5" />
                            <span>Place Hold (No Payment Now)</span>
                          </>
                        )}
                        {!isHoldRequest && selectedPaymentMode === 'CREDIT' && (
                          <>
                            <Building2 className="w-5 h-5" />
                            <span>Confirm & Book with Credit</span>
                          </>
                        )}
                        {!isHoldRequest && selectedPaymentMode === 'WALLET' && (
                          <>
                            <Wallet className="w-5 h-5" />
                            <span>Confirm & Book with Wallet</span>
                          </>
                        )}
                        {!isHoldRequest && selectedPaymentMode === 'GATEWAY' && (
                          <>
                            <CreditCard className="w-5 h-5" />
                            <span>Proceed to Payment</span>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        <span>Proceed to Payment</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
        </div>
          </div>
          {/* End of RIGHT COLUMN */}
        </div>
        {/* End of 2-Column Grid */}
      </div>
      {/* End of MAX-WIDTH Container */}
      
      {/* Mobile Only Fixed Bottom Bar */}
      {/* Compact Sticky Bottom Menu */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] py-2 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Compact Error Message */}
          {error && (
            <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-800 whitespace-pre-line">{error}</p>
            </div>
          )}

          <ReviewMessagesBanner
            formConditions={bookingData?.formConditions}
            testIdSuffix="review-booking-mobile"
          />

          {/* U1 + U2 — Session-expiry chip + exhausted banner above the
              mobile Pay button. The chip auto-hides when the supplier
              doesn't publish a TTL (Riya / offline / hotel paths). */}
          {(_sessionExpiry.hasSupplierTtl || repriceExhausted) && (
            <div className="mb-2 flex flex-col gap-1.5">
              {_sessionExpiry.hasSupplierTtl && (
                <SessionExpiryChip
                  formConditions={bookingData?.formConditions || null}
                  onExpire={() => setSessionExpired(true)}
                  testIdSuffix="review-booking"
                />
              )}
              {repriceExhausted && (
                <div
                  className="rounded-lg border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800"
                  data-testid="reprice-exhausted-banner-review"
                  role="alert"
                >
                  <span className="font-semibold">Fare locked.</span>{' '}
                  Search again — this flight can't be held at the previous price.
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between gap-3">
            {/* Compact Total Display */}
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-gray-500">Total:</span>
              <span className="text-xl md:text-2xl font-bold text-[#1B375B]">₹{totalAmount.toLocaleString()}</span>
            </div>
            
            {/* Compact Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleEditDetails}
                disabled={loading}
                className="px-4 py-2 md:px-5 md:py-2.5 bg-white border border-[#1B375B] text-[#1B375B] rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Edit</span>
              </button>
              
              <button
                onClick={handleProceedToPayment}
                disabled={loading || repriceExhausted || sessionExpired}
                className="px-5 py-2 md:px-6 md:py-2.5 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-md text-sm"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span className="hidden sm:inline">Processing...</span>
                  </>
                ) : (repriceExhausted || sessionExpired) ? (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    <span>Please re-search</span>
                  </>
                ) : (
                  <>
                    {isHoldRequest ? (
                      <>
                        <Clock className="w-4 h-4" />
                        <span>Place Hold</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        <span>Pay Now</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* End of Mobile Bottom Bar */}

      <FareRepriceModal
        open={fareRepriceOpen}
        onClose={() => { setFareRepriceOpen(false); setFareRepriceDetail(null); }}
        detail={fareRepriceDetail}
        priceIds={bookingData?.priceIds || [bookingData?.priceId].filter(Boolean)}
        repriceAttempt={fareRepriceAttempt}
        onRefreshSuccess={handleFareRepriceModalResult}
        source="review_booking"
      />

      {/* B2C OTP login gate — opens when a guest clicks Proceed to Payment.
          After login (existing or new user), auto-resumes the payment flow. */}
      <MobileOTPLogin
        isOpen={otpLoginOpen}
        onClose={() => {
          setOtpLoginOpen(false);
          setResumePaymentAfterLogin(false);
        }}
        onSuccess={() => {
          setOtpLoginOpen(false);
          if (resumePaymentAfterLogin) {
            setResumePaymentAfterLogin(false);
            // Small delay so the AuthContext state propagates before we re-check
            setTimeout(() => { handleProceedToPayment(); }, 150);
          }
        }}
      />

      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
};

export default ReviewBooking;
